import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { setBearer, setCsrfMirror, clearAuthFallback } from "@/lib/api";

const CandidateAuthContext = createContext(null);

export function CandidateAuthProvider({ children }) {
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const resp = await api.get("/candidate/me");
      setCandidate(resp.data);
    } catch (_error) {
      setCandidate(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const login = (candidateData, bearerToken = null, csrfToken = null) => {
    // Backend has already set the cookies via /candidate/verify-magic-link.
    // Preview-environment fallback: mirror bearer + csrf into sessionStorage.
    if (bearerToken) setBearer(bearerToken);
    if (csrfToken) setCsrfMirror(csrfToken);
    setCandidate(candidateData);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      // Logout failures are non-fatal — the local session is cleared below
      // regardless, and a stale server-side session will eventually expire.
      console.warn("CandidateAuth: server logout failed", error);
    }
    clearAuthFallback();
    setCandidate(null);
  };

  const updateProfile = (updates) => {
    setCandidate((prev) => ({ ...prev, ...updates }));
  };

  const value = {
    candidate,
    loading,
    isAuthenticated: !!candidate,
    login,
    logout,
    updateProfile,
    refresh: fetchProfile,
  };

  return (
    <CandidateAuthContext.Provider value={value}>{children}</CandidateAuthContext.Provider>
  );
}

export function useCandidateAuth() {
  const context = useContext(CandidateAuthContext);
  if (!context) {
    throw new Error("useCandidateAuth must be used within CandidateAuthProvider");
  }
  return context;
}
